import fallbackAvatarImage from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { CreateCardModal } from '@/components/CreateCardModal';
import { Image } from '@/components/Image';
import { InstallPwaModal } from '@/components/InstallPwaModal';
import { Loading } from '@/components/Loading';
import { OnboardingTour } from '@/components/Onboarding/OnboardingTour';
import { QrCodeGenerator } from '@/components/QrCodeGenerator';
import { ShareModal } from '@/components/ShareModal';
import { Text } from '@/components/Text';
import { useCards } from '@/hooks/useCards';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { CardsHeader } from '@/partials/dashboard/Cards/ListCards/CardsHeader';
import { PremiumLimit } from '@/partials/dashboard/Cards/UpdateCard/PremiumLimit';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import { Card } from '@/types/card';
import { api } from '@/utils/axios';
import { cn } from '@/utils/cn';
import { isAndroid, isIOS, isPWA } from '@/utils/device';
import getDefaultCover from '@/utils/getDefaultCover';
import { animate, motion, PanInfo, useMotionValue } from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaEdit,
  FaQrcode,
  FaWallet,
} from 'react-icons/fa';
import { IoEyeOutline } from 'react-icons/io5';

const CardView = React.memo(
  ({
    card,
    onModify,
    onAddToWallet,
  }: {
    card: Card;
    onModify: () => void;
    onAddToWallet: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto w-full rounded-3xl bg-white">
        {/* Cover */}
        <div className="relative h-24 w-full rounded-t-3xl">
          <Image
            src={card.cover}
            defaultImage={getDefaultCover(card.color)}
            alt="Cover"
            className="size-full rounded-t-3xl object-cover"
          />
          {/* Visits Count */}
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/50 px-2 py-1 text-xs font-semibold text-white">
            <IoEyeOutline className="size-4" />
            <span>
              {card.monthly_visits_count} /{' '}
              {card.max_visits_count === 'infinite'
                ? '∞'
                : card.max_visits_count}
            </span>
          </div>
          {/* Avatar */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
            <Image
              src={card.avatar}
              defaultImage={fallbackAvatarImage}
              alt="Avatar"
              className="size-16 rounded-full border-4 border-white bg-white object-cover shadow-md md:size-20"
            />
          </div>
        </div>
        {/* Card Content */}
        <div className="flex flex-col items-center px-4 pb-3 pt-12">
          <h3 className="mb-1 text-center text-base font-bold text-gray-900 md:text-lg">
            {card.name}
          </h3>

          {card.job && (
            <p className="mb-1.5 text-center text-xs text-gray-400">
              {card.job}
            </p>
          )}
          <div className="flex w-full flex-col gap-1.5">
            <Button
              id="modify-card-button"
              onClick={onModify}
              className="w-full bg-linko-light text-sm font-medium text-white transition-colors hover:bg-linko-dark"
            >
              <FaEdit className="mr-1 inline" /> {t('common:modify')}
            </Button>
            <Button
              id="add-to-wallet-button"
              onClick={onAddToWallet}
              className="w-full bg-linko-dark text-sm font-medium text-white transition-colors hover:bg-linko-dark"
            >
              <FaWallet className="mr-1 inline" />{' '}
              {t('cardsList:add-to-wallet')}
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

export const CardsList = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation(['cardsList', 'common', 'updateCard']);
  const { limitExceeded, isFetching } = useCardStore();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedCardSlug, setSelectedCardSlug] = useState<string | null>(null);
  const [isInstallPwaModalOpen, setIsInstallPwaModalOpen] = useState(false);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);

  const isLg = useMediaQuery('(min-width: 1024px)');
  const cardWidthFactor = isLg ? 1 / 3 : 0.7;
  const containerPaddingFactor = (1 - cardWidthFactor) / 2;

  const {
    searchValue,
    setSearchValue,
    sortOrder,
    setSortOrder,
    filter,
    cards,
    pagination,
    filterOptions,
    isCreateModalOpen,
    setIsCreateModalOpen,
    handleCreateCard,
    handleModify,
    handleFilterChange,
    handleAddCard,
    showFilterSelect,
    fetchMoreData,
  } = useCards();

  const isMyCards = filter === 'my';

  const hasMoreCards = pagination.current_page < pagination.last_page;

  const displayCards = useMemo(() => {
    if (isFetching && cards.length === 0) {
      return [];
    }

    const newDisplayCards: (Card | 'add-card' | 'loading')[] = [...cards];

    if (isMyCards) {
      newDisplayCards.push('add-card');
    } else if (hasMoreCards) {
      newDisplayCards.push('loading');
    }

    if (
      cards.length === 0 &&
      !isFetching &&
      !newDisplayCards.includes('loading')
    ) {
      return ['add-card'];
    }

    return newDisplayCards;
  }, [cards, isMyCards, hasMoreCards, isFetching]);

  const cardForShareModal = cards.find(card => card.slug === selectedCardSlug);
  const cardSlugForTour = cards[0]?.slug;

  useEffect(() => {
    const isPwaActive = isPWA();
    setShowInstallButton((isIOS() || isAndroid()) && !isPwaActive);
  }, []);

  useEffect(() => {
    if (hasMoreCards && selectedIndex >= cards.length - 5) {
      fetchMoreData();
    }
  }, [selectedIndex, cards.length, hasMoreCards, fetchMoreData]);

  useEffect(() => {
    if (!carouselRef.current) return;

    const cardWidth = carouselRef.current.offsetWidth * cardWidthFactor;
    const containerWidth = carouselRef.current.offsetWidth;
    const newX =
      -selectedIndex * cardWidth + containerWidth * containerPaddingFactor;

    animate(x, newX, {
      type: 'spring' as const,
      stiffness: 500,
      damping: 60,
    });
  }, [
    selectedIndex,
    x,
    cardWidthFactor,
    containerPaddingFactor,
    displayCards.length,
  ]);

  const handleShareClick = (slug: string) => {
    setSelectedCardSlug(slug);
  };

  const goToCard = useCallback(
    (newIndex: number) => {
      if (newIndex < 0 || newIndex >= displayCards.length) return;
      setSelectedIndex(newIndex);
    },
    [displayCards.length],
  );

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (!carouselRef.current) return;
    const { offset } = info;

    const dragThreshold = 2;

    if (offset.x > dragThreshold) {
      goToCard(selectedIndex - 1);
    } else if (offset.x < -dragThreshold) {
      goToCard(selectedIndex + 1);
    } else {
      const cardWidth = carouselRef.current.offsetWidth * cardWidthFactor;
      const containerWidth = carouselRef.current.offsetWidth;
      const newX =
        -selectedIndex * cardWidth + containerWidth * containerPaddingFactor;
      animate(x, newX, {
        type: 'spring' as const,
        stiffness: 500,
        damping: 60,
      });
    }
  };

  const handleAddToWallet = async (slug: string) => {
    try {
      const response = await api.get(`/app-web/cards/${slug}/mobile-pass`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.apple.pkpass',
        },
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'business-card.pkpass';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      link.download = filename;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('cardsList:errorDownloadingWalletPass'));
    }
  };
  return (
    <div className="flex h-full flex-col items-center">
      <OnboardingTour
        cardSlugForTour={cardSlugForTour}
        isCardFetching={pagination.total === 0}
      />
      <div className=" w-full ">
        <div>
          <CardsHeader
            id="cards-header"
            total={pagination.total}
            filter={filter}
            sortOrder={sortOrder}
            searchValue={searchValue}
            showFilterSelect={showFilterSelect}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
            onSortOrderChange={setSortOrder}
            onSearchChange={setSearchValue}
          />
          {limitExceeded && filter !== 'company' && (
            <PremiumLimit
              text={t('updateCard:cards.limit.limit_exceeded')}
              ctaText={t('updateCard:cards.limit.limit_exceeded_cta')}
            />
          )}
        </div>
        {/* État de chargement initial */}
        {isFetching && displayCards.length === 0 && (
          <div className="flex h-[480px] items-center justify-center">
            <Loading />
          </div>
        )}
        {displayCards.length > 0 && (
          <div className="flex flex-col items-center gap-4  md:mt-8 md:gap-6">
            {/* Card Carousel */}
            <div className="flex w-full items-center">
              {/* Left Arrow */}
              <button
                onClick={() => goToCard(selectedIndex - 1)}
                disabled={selectedIndex === 0}
                className={cn(
                  'z-10 mt-40 hidden shrink-0 transition-opacity md:block',
                  selectedIndex === 0
                    ? 'opacity-0'
                    : 'opacity-50 hover:opacity-100',
                )}
              >
                <FaChevronLeft className="size-8 text-gray-600" />
              </button>
              <div
                id="card-carousel"
                className="mx-auto flex w-full max-w-sm items-center justify-center sm:max-w-lg md:max-w-2xl lg:max-w-4xl"
              >
                {/* Carousel Viewport */}
                <div
                  ref={carouselRef}
                  className="h-[480px] w-full overflow-hidden"
                >
                  <motion.div
                    className="flex h-full items-center"
                    drag="x"
                    style={{ x }}
                    dragConstraints={{
                      left: carouselRef.current
                        ? -(displayCards.length - 1) *
                            (carouselRef.current.offsetWidth *
                              cardWidthFactor) +
                          carouselRef.current.offsetWidth *
                            containerPaddingFactor
                        : 0,
                      right: carouselRef.current
                        ? carouselRef.current.offsetWidth *
                          containerPaddingFactor
                        : 0,
                    }}
                    dragElastic={0.1}
                    onDragEnd={handleDragEnd}
                  >
                    {displayCards.map((item, index) => {
                      if (item === 'loading') {
                        return (
                          <motion.div
                            key="loading"
                            className="h-full shrink-0 px-2"
                            style={{ width: `${cardWidthFactor * 100}%` }}
                            animate={{
                              scale: index === selectedIndex ? 1 : 0.85,
                              opacity: index === selectedIndex ? 1 : 0.6,
                            }}
                            transition={{
                              type: 'spring' as const,
                              stiffness: 400,
                              damping: 40,
                            }}
                          >
                            <div className="flex h-full flex-col items-center justify-center gap-4">
                              <div className="h-[188px] w-full" />
                              <Loading />
                            </div>
                          </motion.div>
                        );
                      }
                      if (item === 'add-card') {
                        return (
                          <motion.div
                            key="add-card"
                            className="h-full shrink-0 px-2"
                            style={{ width: `${cardWidthFactor * 100}%` }}
                            animate={{
                              scale: index === selectedIndex ? 1 : 0.85,
                              opacity: index === selectedIndex ? 1 : 0.6,
                            }}
                            transition={{
                              type: 'spring' as const,
                              stiffness: 400,
                              damping: 40,
                            }}
                          >
                            <div className="flex h-full flex-col items-center justify-center gap-2">
                              {/* QR Code disabled simplifié */}
                              <div className="flex items-center justify-center">
                                <div className="cursor-pointer rounded-2xl bg-white p-2 shadow-lg">
                                  <div className="flex size-[80px] w-full items-center justify-center rounded-lg bg-gray-100">
                                    <FaQrcode className="size-8 text-gray-400" />
                                  </div>
                                  <Text className="mt-1 text-center text-xs font-medium text-gray-700">
                                    {t('cardsList:share.scanToShare')}
                                  </Text>
                                </div>
                              </div>

                              {/* Carte d'ajout stylée avec les mêmes dimensions que CardView */}
                              <div
                                id="add-card-button"
                                onClick={handleAddCard}
                                className="group mx-auto flex w-full cursor-pointer flex-col rounded-3xl border-2 border-dashed border-gray-300 bg-white transition-all duration-200 hover:border-linko-light hover:shadow-lg"
                              >
                                {/* Cover avec dégradé - même hauteur que CardView (h-24) */}
                                <div className="relative h-24 w-full rounded-t-3xl bg-gradient-to-r from-gray-100 to-gray-200 transition-all duration-200 group-hover:from-linko-light/20 group-hover:to-linko-dark/20">
                                  {/* Avatar par défaut - même taille que CardView (size-16 mobile, size-20 desktop) */}
                                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                                    <Image
                                      src=""
                                      defaultImage={fallbackAvatarImage}
                                      alt="Avatar"
                                      className="size-16 rounded-full border-4 border-white bg-white object-cover shadow-md transition-all duration-200 md:size-20"
                                    />
                                  </div>
                                </div>

                                {/* Contenu de la carte */}
                                <div className="flex flex-col items-center px-4 pb-2 pt-10">
                                  {cards.length === 0 ? (
                                    <>
                                      <h3 className="mb-1 text-center text-sm font-bold text-gray-700 transition-colors duration-200 group-hover:text-linko-dark">
                                        {t('cardsList:noCardsFound.title')}
                                      </h3>
                                      <p className="mb-2 text-center text-xs text-gray-500 transition-colors duration-200 group-hover:text-gray-600">
                                        {t(
                                          'cardsList:noCardsFound.description',
                                        )}
                                      </p>
                                    </>
                                  ) : (
                                    <h3 className="mb-2 text-center text-sm font-bold text-gray-700 transition-colors duration-200 group-hover:text-linko-dark">
                                      {t('cardsList:createAnotherCard')}
                                    </h3>
                                  )}

                                  {/* Boutons factices */}
                                  <div className="flex w-full flex-col gap-1">
                                    <div className="flex w-full items-center justify-center rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-400 transition-all duration-200 group-hover:bg-linko-light/20 group-hover:text-linko-dark">
                                      <FaEdit className="mr-1 size-2.5" />
                                      {cards.length === 0
                                        ? t('cardsList:createACard')
                                        : t('common:modify')}
                                    </div>
                                    <div className="flex w-full items-center justify-center rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-400 transition-all duration-200 group-hover:bg-linko-dark/20 group-hover:text-linko-dark">
                                      <FaWallet className="mr-1 size-2.5" />
                                      {t('cardsList:add-to-wallet')}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      }

                      const card = item as Card;

                      return (
                        <motion.div
                          key={card.id}
                          className="h-full shrink-0 px-2"
                          style={{ width: `${cardWidthFactor * 100}%` }}
                          animate={{
                            scale: index === selectedIndex ? 1 : 0.85,
                            opacity: index === selectedIndex ? 1 : 0.6,
                          }}
                          transition={{
                            type: 'spring' as const,
                            stiffness: 400,
                            damping: 40,
                          }}
                        >
                          <div className="flex h-full flex-col items-center justify-center gap-3">
                            <motion.div
                              id="share-card-qr"
                              onClick={() => handleShareClick(card.slug)}
                              className="cursor-pointer rounded-2xl bg-white p-3 shadow-lg"
                            >
                              <QrCodeGenerator
                                slug={card.slug}
                                size={100}
                                qrColor={card.qr_code_color}
                              />
                              <Text className="mt-1.5 text-center text-xs font-medium text-gray-700 sm:text-sm">
                                {t('cardsList:share.scanToShare')}
                              </Text>
                            </motion.div>
                            <CardView
                              card={card}
                              onModify={() => handleModify(card.slug)}
                              onAddToWallet={() => handleAddToWallet(card.slug)}
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </div>
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => goToCard(selectedIndex + 1)}
                disabled={selectedIndex === displayCards.length - 1}
                className={cn(
                  'z-10 mt-40 hidden shrink-0 transition-opacity md:block',
                  selectedIndex === displayCards.length - 1
                    ? 'opacity-0'
                    : 'opacity-50 hover:opacity-100',
                )}
              >
                <FaChevronRight className="size-8 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>
      {showInstallButton && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => setIsInstallPwaModalOpen(true)}
            className="group flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
          >
            <FaDownload className="size-3.5 transition-transform duration-200 group-hover:scale-110" />
            <span>{t('cardsList:installApp')}</span>
          </button>
        </div>
      )}
      {/* Modals */}
      <CreateCardModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateForSelf={() => handleCreateCard(user?.id as number)}
        onCreateForUser={handleCreateCard}
      />
      <InstallPwaModal
        isOpen={isInstallPwaModalOpen}
        onClose={() => setIsInstallPwaModalOpen(false)}
      />
      {cardForShareModal && (
        <ShareModal
          isOpen={!!selectedCardSlug}
          onClose={() => setSelectedCardSlug(null)}
          card={cardForShareModal}
        />
      )}
    </div>
  );
};
